import { useState, useRef } from "react";

type TimeRange = "year" | "halfYear" | "quarter" | "month" | "week";

interface DataPoint {
  label: string;
  amount: number;
  volume: number;
}

const generateData = (range: TimeRange): DataPoint[] => {
  if (range === "year") {
    return [
      { label: "1月", amount: 6320, volume: 1580 },
      { label: "2月", amount: 5282, volume: 1320 },
      { label: "3月", amount: 4721, volume: 1180 },
      { label: "4月", amount: 4203, volume: 1050 },
      { label: "5月", amount: 3922, volume: 980 },
      { label: "6月", amount: 3680, volume: 920 },
      { label: "7月", amount: 3522, volume: 880 },
      { label: "8月", amount: 3640, volume: 910 },
      { label: "9月", amount: 4082, volume: 1020 },
      { label: "10月", amount: 4721, volume: 1180 },
      { label: "11月", amount: 5523, volume: 1380 },
      { label: "12月", amount: 6082, volume: 1520 },
    ];
  }
  if (range === "halfYear") {
    return [
      { label: "10月", amount: 4721, volume: 1180 },
      { label: "11月", amount: 5523, volume: 1380 },
      { label: "12月", amount: 6082, volume: 1520 },
      { label: "1月", amount: 6320, volume: 1580 },
      { label: "2月", amount: 5282, volume: 1320 },
      { label: "3月", amount: 4721, volume: 1180 },
    ];
  }
  if (range === "quarter") {
    return [
      { label: "1月", amount: 6320, volume: 1580 },
      { label: "2月", amount: 5282, volume: 1320 },
      { label: "3月", amount: 4721, volume: 1180 },
    ];
  }
  if (range === "month") {
    return Array.from({ length: 4 }, (_, i) => ({
      label: `第${i + 1}周`,
      amount: [1580, 1620, 1480, 1640][i],
      volume: [395, 405, 370, 410][i],
    }));
  }
  return Array.from({ length: 7 }, (_, i) => {
    const days = ["周一", "周二", "周三", "周四", "周五", "周六", "周日"];
    return {
      label: days[i],
      amount: [420, 380, 450, 410, 480, 560, 520][i],
      volume: [105, 95, 112, 102, 120, 140, 130][i],
    };
  });
};

const prevYearData: Record<TimeRange, DataPoint[]> = {
  year: [
    { label: "1月", amount: 5820, volume: 1455 },
    { label: "2月", amount: 4980, volume: 1245 },
    { label: "3月", amount: 4320, volume: 1080 },
    { label: "4月", amount: 3860, volume: 965 },
    { label: "5月", amount: 3560, volume: 890 },
    { label: "6月", amount: 3280, volume: 820 },
    { label: "7月", amount: 3120, volume: 780 },
    { label: "8月", amount: 3360, volume: 840 },
    { label: "9月", amount: 3780, volume: 945 },
    { label: "10月", amount: 4320, volume: 1080 },
    { label: "11月", amount: 5080, volume: 1270 },
    { label: "12月", amount: 5680, volume: 1420 },
  ],
  halfYear: [
    { label: "10月", amount: 4320, volume: 1080 },
    { label: "11月", amount: 5080, volume: 1270 },
    { label: "12月", amount: 5680, volume: 1420 },
    { label: "1月", amount: 5820, volume: 1455 },
    { label: "2月", amount: 4980, volume: 1245 },
    { label: "3月", amount: 4320, volume: 1080 },
  ],
  quarter: [
    { label: "1月", amount: 5820, volume: 1455 },
    { label: "2月", amount: 4980, volume: 1245 },
    { label: "3月", amount: 4320, volume: 1080 },
  ],
  month: [
    { label: "第1周", amount: 1420, volume: 355 },
    { label: "第2周", amount: 1480, volume: 370 },
    { label: "第3周", amount: 1360, volume: 340 },
    { label: "第4周", amount: 1520, volume: 380 },
  ],
  week: [
    { label: "周一", amount: 380, volume: 95 },
    { label: "周二", amount: 350, volume: 87 },
    { label: "周三", amount: 410, volume: 102 },
    { label: "周四", amount: 370, volume: 92 },
    { label: "周五", amount: 440, volume: 110 },
    { label: "周六", amount: 510, volume: 127 },
    { label: "周日", amount: 480, volume: 120 },
  ],
};

const timeRanges: { key: TimeRange; label: string }[] = [
  { key: "year", label: "近一年" },
  { key: "halfYear", label: "近半年" },
  { key: "quarter", label: "近一季" },
  { key: "month", label: "近一月" },
  { key: "week", label: "近一周" },
];

export default function PurchaseHistoryChart() {
  const [activeRange, setActiveRange] = useState<TimeRange>("year");
  const [metric, setMetric] = useState<"amount" | "volume">("amount");
  const [showPrev, setShowPrev] = useState(true);
  const [tooltip, setTooltip] = useState<{ x: number; y: number; idx: number } | null>(null);
  const svgRef = useRef<SVGSVGElement>(null);

  const currentData = generateData(activeRange);
  const prevData = prevYearData[activeRange];

  const allValues = [
    ...currentData.map(d => d[metric]),
    ...(showPrev ? prevData.map(d => d[metric]) : []),
  ];
  const maxVal = Math.max(...allValues) * 1.2;
  const minVal = 0;

  const W = 800;
  const H = 260;
  const padL = 60;
  const padR = 20;
  const padT = 20;
  const padB = 40;
  const chartW = W - padL - padR;
  const chartH = H - padT - padB;

  const toX = (i: number) => padL + (i / (currentData.length - 1)) * chartW;
  const toY = (v: number) => padT + chartH - ((v - minVal) / (maxVal - minVal)) * chartH;

  const buildPath = (data: DataPoint[]) =>
    data.map((d, i) => `${i === 0 ? "M" : "L"} ${toX(i)} ${toY(d[metric])}`).join(" ");

  const buildArea = (data: DataPoint[], color: string) => {
    const pts = data.map((d, i) => `${toX(i)},${toY(d[metric])}`).join(" ");
    const first = `${toX(0)},${toY(data[0][metric])}`;
    const last = `${toX(data.length - 1)},${toY(data[data.length - 1][metric])}`;
    return `M ${first} L ${pts} L ${last} L ${toX(data.length - 1)},${padT + chartH} L ${toX(0)},${padT + chartH} Z`;
  };

  const yTicks = 5;
  const yStep = (maxVal - minVal) / yTicks;

  const currentTotal = currentData.reduce((s, d) => s + d[metric], 0);
  const prevTotal = prevData.reduce((s, d) => s + d[metric], 0);
  const growthRate = (((currentTotal - prevTotal) / prevTotal) * 100).toFixed(1);
  const isGrowth = currentTotal >= prevTotal;

  return (
    <div className="bg-white rounded-xl border border-gray-100 p-5">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div>
          <h3 className="text-sm font-bold text-gray-900">购气历史曲线对比</h3>
          <p className="text-xs text-gray-400 mt-0.5">当期 vs 上一周期同比对比分析</p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1">
            <button
              onClick={() => setMetric("amount")}
              className={`px-3 py-1 text-xs rounded-md transition-colors cursor-pointer whitespace-nowrap ${metric === "amount" ? "bg-white text-gray-900 font-medium" : "text-gray-500"}`}
            >
              购气金额
            </button>
            <button
              onClick={() => setMetric("volume")}
              className={`px-3 py-1 text-xs rounded-md transition-colors cursor-pointer whitespace-nowrap ${metric === "volume" ? "bg-white text-gray-900 font-medium" : "text-gray-500"}`}
            >
              用气量
            </button>
          </div>
          <label className="flex items-center gap-1.5 text-xs text-gray-500 cursor-pointer">
            <input type="checkbox" checked={showPrev} onChange={e => setShowPrev(e.target.checked)} className="accent-orange-400" />
            显示上期对比
          </label>
        </div>
      </div>

      {/* Time Range Tabs */}
      <div className="flex items-center gap-1 mb-4 bg-gray-50 rounded-lg p-1 w-fit">
        {timeRanges.map(r => (
          <button
            key={r.key}
            onClick={() => setActiveRange(r.key)}
            className={`px-4 py-1.5 text-xs rounded-md transition-colors cursor-pointer whitespace-nowrap ${activeRange === r.key ? "bg-[#E8531A] text-white font-medium" : "text-gray-500 hover:text-gray-700"}`}
          >
            {r.label}
          </button>
        ))}
      </div>

      {/* Stats Row */}
      <div className="grid grid-cols-4 gap-3 mb-4">
        <div className="bg-orange-50 rounded-lg p-3">
          <p className="text-xs text-gray-500">当期合计</p>
          <p className="text-base font-bold text-gray-900 mt-0.5">
            {metric === "amount" ? `¥${currentTotal.toLocaleString()}` : `${currentTotal.toLocaleString()} m³`}
          </p>
        </div>
        <div className="bg-gray-50 rounded-lg p-3">
          <p className="text-xs text-gray-500">上期合计</p>
          <p className="text-base font-bold text-gray-500 mt-0.5">
            {metric === "amount" ? `¥${prevTotal.toLocaleString()}` : `${prevTotal.toLocaleString()} m³`}
          </p>
        </div>
        <div className={`rounded-lg p-3 ${isGrowth ? "bg-red-50" : "bg-green-50"}`}>
          <p className="text-xs text-gray-500">同比变化</p>
          <p className={`text-base font-bold mt-0.5 ${isGrowth ? "text-red-600" : "text-green-600"}`}>
            {isGrowth ? "+" : ""}{growthRate}%
          </p>
        </div>
        <div className="bg-blue-50 rounded-lg p-3">
          <p className="text-xs text-gray-500">期均值</p>
          <p className="text-base font-bold text-gray-700 mt-0.5">
            {metric === "amount"
              ? `¥${(currentTotal / currentData.length).toFixed(0)}`
              : `${(currentTotal / currentData.length).toFixed(0)} m³`}
          </p>
        </div>
      </div>

      {/* SVG Chart */}
      <div className="relative overflow-x-auto">
        <svg
          ref={svgRef}
          viewBox={`0 0 ${W} ${H}`}
          className="w-full"
          style={{ minWidth: 480 }}
          onMouseLeave={() => setTooltip(null)}
        >
          <defs>
            <linearGradient id="gradCurrent" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#E8531A" stopOpacity="0.18" />
              <stop offset="100%" stopColor="#E8531A" stopOpacity="0.01" />
            </linearGradient>
            <linearGradient id="gradPrev" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#94a3b8" stopOpacity="0.15" />
              <stop offset="100%" stopColor="#94a3b8" stopOpacity="0.01" />
            </linearGradient>
          </defs>

          {/* Y grid lines */}
          {Array.from({ length: yTicks + 1 }, (_, i) => {
            const v = minVal + yStep * i;
            const y = toY(v);
            return (
              <g key={i}>
                <line x1={padL} y1={y} x2={W - padR} y2={y} stroke="#f1f5f9" strokeWidth="1" />
                <text x={padL - 6} y={y + 4} textAnchor="end" fontSize="10" fill="#94a3b8">
                  {metric === "amount" ? `¥${(v / 1000).toFixed(0)}k` : `${v.toFixed(0)}`}
                </text>
              </g>
            );
          })}

          {/* X axis labels */}
          {currentData.map((d, i) => (
            <text key={i} x={toX(i)} y={H - 8} textAnchor="middle" fontSize="10" fill="#94a3b8">
              {d.label}
            </text>
          ))}

          {/* Area fills */}
          {showPrev && (
            <path d={buildArea(prevData, "#94a3b8")} fill="url(#gradPrev)" />
          )}
          <path d={buildArea(currentData, "#E8531A")} fill="url(#gradCurrent)" />

          {/* Lines */}
          {showPrev && (
            <path d={buildPath(prevData)} fill="none" stroke="#cbd5e1" strokeWidth="1.5" strokeDasharray="4 3" />
          )}
          <path d={buildPath(currentData)} fill="none" stroke="#E8531A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />

          {/* Dots + hover */}
          {currentData.map((d, i) => (
            <g key={i}>
              <circle
                cx={toX(i)}
                cy={toY(d[metric])}
                r="4"
                fill="white"
                stroke="#E8531A"
                strokeWidth="2"
                className="cursor-pointer"
                onMouseEnter={() => setTooltip({ x: toX(i), y: toY(d[metric]), idx: i })}
              />
            </g>
          ))}

          {/* Tooltip */}
          {tooltip !== null && (() => {
            const d = currentData[tooltip.idx];
            const p = prevData[tooltip.idx];
            const tx = tooltip.x > W - 160 ? tooltip.x - 140 : tooltip.x + 10;
            const ty = tooltip.y < 80 ? tooltip.y + 10 : tooltip.y - 80;
            return (
              <g>
                <rect x={tx} y={ty} width="130" height="72" rx="6" fill="white" stroke="#e2e8f0" strokeWidth="1" />
                <text x={tx + 10} y={ty + 18} fontSize="11" fontWeight="600" fill="#1e293b">{d.label}</text>
                <text x={tx + 10} y={ty + 34} fontSize="10" fill="#E8531A">
                  当期: {metric === "amount" ? `¥${d.amount.toLocaleString()}` : `${d.volume} m³`}
                </text>
                <text x={tx + 10} y={ty + 50} fontSize="10" fill="#94a3b8">
                  上期: {metric === "amount" ? `¥${p.amount.toLocaleString()}` : `${p.volume} m³`}
                </text>
                <text x={tx + 10} y={ty + 64} fontSize="10" fill={d[metric] >= p[metric] ? "#ef4444" : "#22c55e"}>
                  {d[metric] >= p[metric] ? "▲" : "▼"} {(((d[metric] - p[metric]) / p[metric]) * 100).toFixed(1)}%
                </text>
              </g>
            );
          })()}
        </svg>
      </div>

      {/* Legend */}
      <div className="flex items-center gap-5 mt-2">
        <div className="flex items-center gap-1.5">
          <div className="w-6 h-0.5 bg-[#E8531A] rounded"></div>
          <span className="text-xs text-gray-500">当期</span>
        </div>
        {showPrev && (
          <div className="flex items-center gap-1.5">
            <div className="w-6 h-0.5 bg-gray-300 rounded" style={{ borderTop: "1.5px dashed #cbd5e1" }}></div>
            <span className="text-xs text-gray-500">上期同比</span>
          </div>
        )}
      </div>
    </div>
  );
}
